import { useEffect, useState } from "react"
import Dialog from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import Textarea from "@/v2-ui/textarea"
import Button from "@/v2-backoffice-client/components/button"
import {
  PRIVACY_JUSTIFICATION_HINT,
  PRIVACY_JUSTIFICATION_LABEL,
  PRIVACY_JUSTIFICATION_MAX_LENGTH,
} from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.constants"
import type { PrivacyPreset } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.types"

export type TenantPrivacyApplyPresetDialogProps = {
  /** The preset being applied; null keeps the dialog closed. */
  preset: PrivacyPreset | null
  hasUnsavedEdits: boolean
  isApplying: boolean
  onClose(): void
  onConfirm(justification: string | null): void
}

// The confirm step before a preset replaces the team's whole policy. States
// exactly what the apply does — including the two things that surprise:
// uncovered sources get reset, unsaved table edits are discarded — and takes
// the optional justification the change history keeps.
function TenantPrivacyApplyPresetDialog(
  props: TenantPrivacyApplyPresetDialogProps,
) {
  const { preset, hasUnsavedEdits, isApplying, onClose, onConfirm } = props

  const isOpen = preset !== null
  const [justification, setJustification] = useState("")

  // Re-arm on every opening. The editor closes the dialog by nulling
  // `preset` after a successful apply, which never reaches onOpenChange
  // (Radix only fires it for user-initiated closes), so clearing on close
  // would carry one preset's justification into the next one's audit rows.
  useEffect(() => {
    if (isOpen) setJustification("")
  }, [isOpen])

  // Radix keeps the content mounted for the exit animation after `preset`
  // goes null; keep showing the preset that was open so the title does not
  // collapse to an empty name mid-fade.
  const [shownPreset, setShownPreset] = useState(preset)
  if (preset && preset !== shownPreset) setShownPreset(preset)

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>Apply &quot;{shownPreset?.Name}&quot;</Dialog.Title>
          <Dialog.Description>{shownPreset?.Description}</Dialog.Description>
        </Dialog.Header>

        <div className="flex flex-col gap-3 py-2 text-sm">
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li>
              Replaces the team&apos;s per-source settings so they exactly match
              the preset.
            </li>
            {shownPreset &&
              shownPreset.FixedPrivateDataVisibilityTypeCode == null && (
                <li>
                  Sources the preset has no rule for are reset to hidden — it
                  never leaves anything more open than it says.
                </li>
              )}
            {hasUnsavedEdits && (
              <li className="font-medium text-foreground">
                Your unsaved per-source edits are discarded.
              </li>
            )}
          </ul>

          <div className="flex flex-col gap-1">
            <Label htmlFor="privacyPresetJustification">
              {PRIVACY_JUSTIFICATION_LABEL}
            </Label>
            <Textarea
              id="privacyPresetJustification"
              rows={3}
              maxLength={PRIVACY_JUSTIFICATION_MAX_LENGTH}
              placeholder={PRIVACY_JUSTIFICATION_HINT}
              value={justification}
              onChange={(event) => {
                setJustification(event.target.value)
              }}
            />
          </div>
        </div>

        <Dialog.Footer>
          <Button
            type="button"
            variant="outline"
            disabled={isApplying}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isApplying}
            isLoading={isApplying}
            data-feature-code="UI_BackofficeTenantPrivacy_PresetDialog_Confirm"
            onClick={() => {
              onConfirm(justification.trim() || null)
            }}
          >
            Apply preset
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default TenantPrivacyApplyPresetDialog
